/**
 * Body/HTML editor for entity forms (EmailTemplate Body and Content entity Body).
 * Encapsulates Ace, Monaco, Quill, Raw, and Original Raw modes (emailTemplate) or Source/Preview/Side-by-side (content).
 */

import React, { useState, useRef, useEffect } from 'react';
import { Box, Group, Text, Paper, Select, ActionIcon, Tabs, Stack, Flex } from '@mantine/core';
import { IconMinimize, IconMaximize } from '@tabler/icons-react';
import { formatHTMLSimple, extractBodyContent, reconstructFullHTML, parseHTMLDocument, reconstructHTMLFromParts } from '../../utils/htmlFormatter';
import { FieldLabelWithRequired } from './renderDynamicField';
import { ScimAceEditor } from '../common/ScimAceEditor';
import { ScimMonacoEditor } from '../common/ScimMonacoEditor';
import { QuillEditor } from '../common/QuillEditor';
import { HtmlPreview } from '../common/HtmlPreview';
import { useContentBodyDeepLink } from '../../hooks/useContentBodyDeepLink';

export type BodyHtmlEditorVariant = 'emailTemplate' | 'content';

type EditorMode = 'ace' | 'quill' | 'monaco' | 'raw' | 'originalRaw';

export interface BodyHtmlEditorProps {
    /** Field name (e.g. "Body") for stable keys and refs */
    fieldName: string;
    label: string;
    value: string;
    onChange: (value: string) => void;
    isRequired?: boolean;
    disabled?: boolean;
    /** For "Original Raw" view in emailTemplate variant; optional in create mode */
    originalValue?: string;
    /** emailTemplate = full mode switcher (Ace/Monaco/Quill/Raw/OriginalRaw); content = Source/Preview/Side-by-side tabs */
    variant: BodyHtmlEditorVariant;
    /** Content entity Body: sync fullscreen with hash ?contentBody=fullScreen */
    contentBodyDeepLink?: boolean;
}

function getEffectiveValue(
    value: string,
    formattedValue: string | undefined,
    originalFormattedValue: string | undefined
): string {
    if (formattedValue !== undefined) return formattedValue;
    if (originalFormattedValue) return originalFormattedValue;
    return value;
}

export const BodyHtmlEditor: React.FC<BodyHtmlEditorProps> = ({
    fieldName,
    label,
    value,
    onChange,
    isRequired = false,
    disabled = false,
    originalValue = '',
    variant,
    contentBodyDeepLink = false,
}) => {
    const [editorMode, setEditorMode] = useState<EditorMode>('quill');
    const useDeepLink = variant === 'content' && contentBodyDeepLink;
    const { isFullscreen, toggleFullscreen } = useContentBodyDeepLink(useDeepLink);
    const [localFullscreen, setLocalFullscreen] = useState(false);
    const isFullscreenEffective = useDeepLink ? isFullscreen : localFullscreen;
    const [formattedValue, setFormattedValue] = useState<string | undefined>(undefined);
    const [originalFormattedValue, setOriginalFormattedValue] = useState<string | undefined>(undefined);
    const [originalFullHTML, setOriginalFullHTML] = useState<string | undefined>(undefined);
    const [monacoTab, setMonacoTab] = useState<0 | 1 | 2>(0);
    const [contentTab, setContentTab] = useState(0);
    const quillInitialValueRef = useRef<string>('');
    const quillChangeCountRef = useRef<number>(0);
    const quillUserInteractedRef = useRef<boolean>(false);
    const previousEditorModeRef = useRef<EditorMode>('ace');
    const previousValueRef = useRef<string>('');

    const currentValue = typeof value === 'string' ? value : '';
    const editorHeight = isFullscreenEffective ? 'calc(100vh - 120px)' : '400px';
    const fullscreenStyle = isFullscreenEffective
        ? { position: 'fixed' as const, top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, backgroundColor: 'var(--mantine-color-body)', padding: 16 }
        : undefined;

    // On first load in ace mode, store formatted value
    useEffect(() => {
        if (editorMode === 'ace' && currentValue && originalFormattedValue === undefined && formattedValue === undefined) {
            setOriginalFormattedValue(formatHTMLSimple(currentValue));
        }
    }, [editorMode, currentValue, originalFormattedValue, formattedValue]);

    // If parent resets the value (e.g. dialog "Discard Changes"), clear any cached editor formatting state
    // so the visible editor content actually reflects the new prop value.
    useEffect(() => {
        const prev = previousValueRef.current;
        previousValueRef.current = currentValue;

        if (variant !== 'emailTemplate') return;
        if (!prev || prev === currentValue) return;

        const orig = typeof originalValue === 'string' ? originalValue : '';
        const looksLikeExternalReset = orig !== '' && currentValue === orig;
        if (!looksLikeExternalReset) return;

        setFormattedValue(undefined);
        setOriginalFormattedValue(undefined);
        setOriginalFullHTML(undefined);

        quillInitialValueRef.current = extractBodyContent(currentValue);
        quillChangeCountRef.current = 0;
        quillUserInteractedRef.current = false;
    }, [currentValue, originalValue, variant]);

    const handleModeChange = (newMode: EditorMode) => {
        const previousMode = previousEditorModeRef.current;

        if (previousMode === 'quill' && newMode === 'ace') {
            const userInteracted = quillUserInteractedRef.current;
            if (!userInteracted && originalFormattedValue) {
                setFormattedValue(originalFormattedValue);
                onChange(originalFormattedValue);
            } else if (currentValue) {
                const formatted = formatHTMLSimple(currentValue);
                setFormattedValue(formatted);
                onChange(formatted);
            }
        } else if (newMode === 'quill' && previousMode === 'ace') {
            if (currentValue && !originalFormattedValue) {
                setOriginalFormattedValue(currentValue);
            }
            if (currentValue && !originalFullHTML) {
                const isFullDocument = currentValue.toLowerCase().includes('<html') || currentValue.toLowerCase().includes('<!doctype');
                if (isFullDocument) setOriginalFullHTML(currentValue);
            }
            quillInitialValueRef.current = extractBodyContent(currentValue);
            quillChangeCountRef.current = 0;
            quillUserInteractedRef.current = false;
            setFormattedValue(undefined);
        }

        if (newMode === 'raw' || newMode === 'originalRaw') {
            if (currentValue && !formattedValue) {
                setFormattedValue(formatHTMLSimple(currentValue));
            }
        }

        previousEditorModeRef.current = newMode;
        setEditorMode(newMode);
    };

    const effectiveValue = getEffectiveValue(currentValue, formattedValue, originalFormattedValue);

    if (variant === 'content') {
        const tabValue = Math.min(contentTab, 2);
        return (
            <Box key={fieldName} mb="md" style={fullscreenStyle}>
                <Group justify="space-between" align="center" mb="xs">
                    <Text size="sm" fw={500}>
                        <FieldLabelWithRequired label={label} required={isRequired} readOnly={disabled} />
                    </Text>
                    <ActionIcon
                        variant="subtle"
                        size="sm"
                        onClick={() => (useDeepLink ? toggleFullscreen() : setLocalFullscreen((f) => !f))}
                        title={isFullscreenEffective ? 'Exit Fullscreen' : 'Enter Fullscreen'}
                    >
                        {isFullscreenEffective ? <IconMinimize size={18} /> : <IconMaximize size={18} />}
                    </ActionIcon>
                </Group>
                <Paper withBorder style={isFullscreenEffective ? { height: 'calc(100vh - 100px)' } : undefined}>
                    <Tabs value={String(tabValue)} onChange={v => setContentTab(Number(v ?? 0))}>
                        <Tabs.List style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>
                            <Tabs.Tab value="0">Source</Tabs.Tab>
                            <Tabs.Tab value="1">Preview</Tabs.Tab>
                            <Tabs.Tab value="2">Side by side</Tabs.Tab>
                        </Tabs.List>
                        <Tabs.Panel value="0">
                            <ScimAceEditor
                                mode="html"
                                value={currentValue}
                                onChange={v => onChange(v ?? '')}
                                name={`html-editor-${fieldName}`}
                                height={editorHeight}
                                fontSize={14}
                                showPrintMargin={false}
                                readOnly={disabled}
                            />
                        </Tabs.Panel>
                        <Tabs.Panel value="1">
                            <Box style={{ height: editorHeight }} tabIndex={0}>
                                <HtmlPreview content={currentValue} emptyMessage="No content to preview" height={editorHeight} />
                            </Box>
                        </Tabs.Panel>
                        <Tabs.Panel value="2">
                            <Flex style={{ height: editorHeight }}>
                                <Box style={{ flex: 1, borderRight: '1px solid var(--mantine-color-default-border)' }}>
                                    <ScimAceEditor
                                        mode="html"
                                        value={currentValue}
                                        onChange={v => onChange(v ?? '')}
                                        name={`html-editor-side-${fieldName}`}
                                        height={editorHeight}
                                        fontSize={14}
                                        showPrintMargin={false}
                                        readOnly={disabled}
                                    />
                                </Box>
                                <Box style={{ flex: 1, overflow: 'hidden' }}>
                                    <HtmlPreview content={currentValue} emptyMessage="No content to preview" height={editorHeight} />
                                </Box>
                            </Flex>
                        </Tabs.Panel>
                    </Tabs>
                </Paper>
            </Box>
        );
    }

    // emailTemplate variant
    const monacoEditorHeight = isFullscreenEffective ? 'calc(100vh - 180px)' : '400px';
    const onAceChange = (newValue: string | undefined) => {
        setFormattedValue(undefined);
        setOriginalFormattedValue(undefined);
        onChange(newValue ?? '');
    };

    return (
        <Box key={fieldName} mb="md" style={fullscreenStyle}>
            <Group justify="space-between" align="center" mb="xs">
                <Text size="sm" fw={500}>
                    <FieldLabelWithRequired label={label} required={isRequired} readOnly={disabled} />
                </Text>
                <Group gap="xs">
                    <Select
                        value={editorMode}
                        onChange={v => handleModeChange((v ?? 'ace') as EditorMode)}
                        disabled={disabled}
                        data={[
                            { value: 'ace', label: 'Code Editor (Ace)' },
                            { value: 'monaco', label: 'Code Editor (Monaco)' },
                            { value: 'quill', label: 'WYSIWYG Editor (Quill)' },
                            { value: 'raw', label: 'Current Raw (Read Only)' },
                            { value: 'originalRaw', label: 'Original Raw (Read Only)' },
                        ]}
                        miw={120}
                    />
                    <ActionIcon
                        variant="subtle"
                        size="sm"
                        onClick={() => setLocalFullscreen((f) => !f)}
                        title={isFullscreenEffective ? 'Exit Fullscreen' : 'Enter Fullscreen'}
                    >
                        {isFullscreenEffective ? <IconMinimize size={18} /> : <IconMaximize size={18} />}
                    </ActionIcon>
                </Group>
            </Group>
            <Paper withBorder style={isFullscreenEffective ? { height: 'calc(100vh - 100px)' } : undefined}>
                {editorMode === 'raw' && (
                    <ScimAceEditor
                        mode="html"
                        value={formatHTMLSimple(effectiveValue)}
                        name={`html-raw-${fieldName}`}
                        height={isFullscreenEffective ? 'calc(100vh - 120px)' : '400px'}
                        fontSize={14}
                        showPrintMargin={false}
                        readOnly={true}
                    />
                )}
                {editorMode === 'originalRaw' && (
                    <ScimAceEditor
                        mode="html"
                        value={formatHTMLSimple(typeof originalValue === 'string' ? originalValue : '')}
                        name={`html-original-raw-${fieldName}`}
                        height={isFullscreenEffective ? 'calc(100vh - 120px)' : '400px'}
                        fontSize={14}
                        showPrintMargin={false}
                        readOnly={true}
                    />
                )}
                {editorMode === 'monaco' && (
                    <Box>
                        <Tabs value={String(monacoTab)} onChange={v => setMonacoTab(Number(v ?? 0) as 0 | 1 | 2)}>
                            <Tabs.List style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>
                                <Tabs.Tab value="0">Split View</Tabs.Tab>
                                <Tabs.Tab value="1">Editor</Tabs.Tab>
                                <Tabs.Tab value="2">Preview</Tabs.Tab>
                            </Tabs.List>
                            <Tabs.Panel value="0">
                                <Flex style={{ height: monacoEditorHeight }}>
                                    <Box style={{ flex: 1, borderRight: '1px solid var(--mantine-color-default-border)' }}>
                                        <ScimMonacoEditor
                                            language="html"
                                            value={effectiveValue}
                                            onChange={onAceChange}
                                            height={monacoEditorHeight}
                                            fontSize={14}
                                            showLineNumbers={true}
                                            showMinimap={false}
                                            wordWrap="on"
                                            readOnly={disabled}
                                        />
                                    </Box>
                                    <Box style={{ flex: 1, overflow: 'hidden' }}>
                                        <HtmlPreview content={effectiveValue ?? ''} emptyMessage="Start typing HTML to see preview..." height="100%" />
                                    </Box>
                                </Flex>
                            </Tabs.Panel>
                            <Tabs.Panel value="1">
                                <ScimMonacoEditor
                                    language="html"
                                    value={effectiveValue}
                                    onChange={onAceChange}
                                    height={monacoEditorHeight}
                                    fontSize={14}
                                    showLineNumbers={true}
                                    showMinimap={false}
                                    wordWrap="on"
                                    readOnly={disabled}
                                />
                            </Tabs.Panel>
                            <Tabs.Panel value="2">
                                <HtmlPreview content={effectiveValue ?? ''} emptyMessage="No content to preview" height={monacoEditorHeight} />
                            </Tabs.Panel>
                        </Tabs>
                    </Box>
                )}
                {editorMode === 'quill' && (
                    <QuillEditor
                        value={extractBodyContent(currentValue)}
                        onChange={newValue => {
                            const initialVal = quillInitialValueRef.current;
                            const count = quillChangeCountRef.current + 1;
                            quillChangeCountRef.current = count;

                            if (count === 1 && initialVal) {
                                const normInitial = initialVal.replace(/\s+/g, ' ').trim();
                                const normNew = newValue.replace(/\s+/g, ' ').trim();
                                if (normInitial === normNew) return;
                                quillUserInteractedRef.current = true;
                            } else if (count > 1) {
                                quillUserInteractedRef.current = true;
                            }

                            const fullHTML = reconstructFullHTML(newValue, originalFullHTML || currentValue);
                            onChange(fullHTML);
                        }}
                        height={isFullscreenEffective ? 'calc(100vh - 120px)' : '400px'}
                        disabled={disabled}
                        placeholder="Enter email body content..."
                        name={`quill-${fieldName}`}
                    />
                )}
                {editorMode === 'ace' && (() => {
                    const htmlParts = parseHTMLDocument(effectiveValue);
                    if (htmlParts.isFullDocument) {
                        return (
                            <Stack gap="sm" style={{ height: isFullscreenEffective ? 'calc(100vh - 120px)' : '400px' }}>
                                <Box flex="0 0 auto" p="sm" bg="var(--mantine-color-default-hover)" style={{ borderRadius: 'var(--mantine-radius-sm)' }}>
                                    <Text size="xs" fw={700}>DOCTYPE & HTML Tag</Text>
                                    <ScimAceEditor
                                        mode="html"
                                        value={`${htmlParts.doctype}\n${htmlParts.htmlTag}`}
                                        onChange={newValue => {
                                            const lines = newValue.split('\n');
                                            const newDoctype = lines.find(l => l.toLowerCase().includes('doctype')) || '';
                                            const newHtmlTag = lines.find(l => l.toLowerCase().includes('<html')) || '<html>';
                                            const updated = { ...htmlParts, doctype: newDoctype, htmlTag: newHtmlTag };
                                            onChange(reconstructHTMLFromParts(updated));
                                        }}
                                        name={`html-doctype-${fieldName}`}
                                        height="80px"
                                        fontSize={12}
                                        showPrintMargin={false}
                                        readOnly={disabled}
                                    />
                                </Box>
                                <Flex gap="sm" style={{ flex: '1 1 auto', minHeight: 0 }}>
                                    <Stack gap="sm" style={{ flex: '0 0 30%' }}>
                                        <Box flex="0 0 auto" p="sm" bg="var(--mantine-color-default-hover)" style={{ borderRadius: 'var(--mantine-radius-sm)' }}>
                                            <Text size="xs" fw={700}>HEAD (not editable in Quill)</Text>
                                        </Box>
                                        <Box style={{ flex: '1 1 auto', minHeight: 0 }}>
                                            <ScimAceEditor
                                                mode="html"
                                                value={htmlParts.headContent || ''}
                                                onChange={v => onChange(reconstructHTMLFromParts({ ...htmlParts, headContent: v }))}
                                                name={`html-head-${fieldName}`}
                                                height="100%"
                                                fontSize={12}
                                                showPrintMargin={false}
                                                readOnly={disabled}
                                            />
                                        </Box>
                                    </Stack>
                                    <Stack gap="sm" style={{ flex: '1 1 auto', minHeight: 0 }}>
                                        <Box flex="0 0 auto" p="sm" bg="var(--mantine-color-blue-1)" style={{ borderRadius: 'var(--mantine-radius-sm)' }}>
                                            <Text size="xs" fw={700}>BODY (editable in Quill)</Text>
                                        </Box>
                                        <Box style={{ flex: '1 1 auto', minHeight: 0 }}>
                                            <ScimAceEditor
                                                mode="html"
                                                value={htmlParts.bodyContent}
                                                onChange={v => onChange(reconstructHTMLFromParts({ ...htmlParts, bodyContent: v }))}
                                                name={`html-body-${fieldName}`}
                                                height="100%"
                                                fontSize={12}
                                                showPrintMargin={false}
                                                readOnly={disabled}
                                            />
                                        </Box>
                                    </Stack>
                                </Flex>
                                {htmlParts.closingTags && (
                                    <Box flex="0 0 auto" p="sm" bg="var(--mantine-color-default-hover)" style={{ borderRadius: 'var(--mantine-radius-sm)' }}>
                                        <Text size="xs" fw={700}>Closing Tags (after &lt;/body&gt;)</Text>
                                        <ScimAceEditor
                                            mode="html"
                                            value={htmlParts.closingTags}
                                            onChange={v => onChange(reconstructHTMLFromParts({ ...htmlParts, closingTags: v }))}
                                            name={`html-closing-${fieldName}`}
                                            height="60px"
                                            fontSize={12}
                                            showPrintMargin={false}
                                            readOnly={disabled}
                                        />
                                    </Box>
                                )}
                            </Stack>
                        );
                    }
                    return (
                        <ScimAceEditor
                            mode="html"
                            value={effectiveValue}
                            onChange={onAceChange}
                            name={`html-editor-${fieldName}`}
                            height={isFullscreenEffective ? 'calc(100vh - 120px)' : '400px'}
                            fontSize={14}
                            showPrintMargin={false}
                            readOnly={disabled}
                        />
                    );
                })()}
            </Paper>
        </Box>
    );
};
